<script setup>
// TODO: Consolidate the code for map vs list views duplicated here and in the <FavoritesHeader>
// in a common component, composable, module, etc.

import { ButtonTypes } from '@@/components/Common/Button.vue';
import { addQueryParamsToPath } from '@@/utils/CommonUtils';
import {
  addCompareStateToQuery,
  addViewToComparePath,
} from '@@/components/Compare/Common/CompareUtils';
import { useLocationsStore } from '@@/stores/Locations';
import { useUserFavoritesStore } from '@@/stores/UserFavorites';
import { useUser } from '@@/composables/useUser';
import { computed } from 'vue';

const props = defineProps({
  compareField: {
    type: Object,
    default: null,
  },
  filter: {
    type: Object,
    default: null,
  },
  isMapPage: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();

const locationsStore = useLocationsStore();
const userFavoritesStore = useUserFavoritesStore();

const isCompareCountries = computed(() => locationsStore.isCompareCountries);
const isCompareGlobal = computed(() => locationsStore.isCompareGlobal);
const isCompareRegions = computed(() => locationsStore.isCompareRegions);
const isCompareSeasonPasses = computed(() => locationsStore.isCompareSeasonPasses);
const isCompareStates = computed(() => locationsStore.isCompareStates);

const compare = computed(() => locationsStore.compare);
const { isGuest } = useUser();

const buttonTextClass = 'tw-hidden lg:tw-inline';
const country = computed(() => {
  if (isCompareStates.value) {
    const [country] = compare.value?.countries || [];
    return country ?? null;
  }

  return null;
});

const myAddCompareStateToQuery = (url) => {
  const { compareField, filter } = props;
  return addCompareStateToQuery(url, { compareField, filter });
};

const countryUrl = computed(() => {
  if (country.value?.code) {
    const url = `/explore/countries/${country.value.code}`;

    if (props.isMapPage) {
      return myAddCompareStateToQuery(`${url}/map`);
    }

    const currentPath = route.fullPath;
    return addViewToComparePath(url, { currentPath });
  }

  return null;
});

const seasonPass = computed(() => isCompareSeasonPasses.value
  ? compare.value.season_passes[0]
  : null);

const isPowderFinderPage = computed(() => route.path.startsWith('/explore/powder'));

const isUserFavoriteSeasonPass = computed(() => isCompareSeasonPasses.value
  ? userFavoritesStore.isFavoriteSeasonPass({ seasonPassId: seasonPass.value?.id })
  : false);

const favoriteButton = computed(() => {
  const return_to = route.fullPath;
  const registerUrl = addQueryParamsToPath('/user/register', { return_to });
  const text = isUserFavoriteSeasonPass.value ? 'Update Favorites' : 'Add to Favorites';

  return {
    icon: isUserFavoriteSeasonPass.value ? ['fas', 'pencil-alt'] : ['far', 'star'],
    text,
    to: isGuest.value ? registerUrl : null,
  };
});

const headingText = computed(() => {
  if (isPowderFinderPage.value) {
    return 'Powder Finder';
  }

  if (isCompareGlobal.value) {
    return 'Global';
  }

  if (isCompareCountries.value) {
    return compare.value?.countries?.[0].name;
  }

  if (isCompareRegions.value) {
    return compare.value?.regions?.[0]?.name;
  }

  if (isCompareSeasonPasses.value) {
    return compare.value?.season_passes?.[0]?.name;
  }

  if (isCompareStates.value) {
    return compare.value?.states?.[0]?.name;
  }

  return '';
});

const actionButtonText = computed(() => props.isMapPage ? 'View List' : 'View Map');

const actionButtonUrl = computed(() => {
  if (isPowderFinderPage.value) {
    const url = `/explore/powder${props.isMapPage ? '' : '/map'}`;
    return myAddCompareStateToQuery(url);
  }

  const url = `/explore/${compare.value?.compareType}/${compare.value?.compareId}`;

  if (props.isMapPage) {
    return url;
  }

  return myAddCompareStateToQuery(`${url}/map`);
});

const handleFavoriteButtonClick = async ($event) => {
  if (isGuest.value) {
    return;
  }

  const { originalEvent } = $event;
  originalEvent.preventDefault();
  originalEvent.stopImmediatePropagation();

  const action = isUserFavoriteSeasonPass.value ? 'update' : 'add';
  const defaultSelectedLists = userFavoritesStore.getFavoriteListsForSeasonPass(seasonPass.value);

  userFavoritesStore.showSelectFavoriteListDialog({
    action,
    defaultSelectedLists,
    seasonPass,
  });

  originalEvent.target.blur();
};
</script>

<template>
  <div class="tw-flex tw-justify-between tw-items-center tw-my-2 lg:tw-my-4">
    <div>
      <h1 class="page-title heading-color">
        {{ headingText }}
      </h1>
      <h2
        v-if="country"
        class="tw-text-lg tw-font-normal"
      >
        <NuxtLink
          class="link-color-brand"
          :to="countryUrl"
        >
          {{ country.name }}
        </NuxtLink>
      </h2>
    </div>
    <div
      class="tw-flex lg:tw-block lg:tw-text-center"
    >
      <Button
        additional-class-names="action-button"
        :small="true"
        :to="actionButtonUrl"
        :type="ButtonTypes.card"
      >
        <font-awesome-icon
          v-if="isMapPage"
          :icon="['fas', 'list']"
        />
        <font-awesome-icon
          v-else
          :icon="['fas', 'globe-americas']"
        />
        <span :class="buttonTextClass">{{ actionButtonText }}</span>
      </Button>
      <Button
        v-if="isCompareSeasonPasses"
        additional-class-names="action-button"
        :small="true"
        :to="favoriteButton.to"
        :type="ButtonTypes.card"
        @click="handleFavoriteButtonClick"
      >
        <font-awesome-icon
          class="action-button-icon tw-mr-0"
          :icon="favoriteButton.icon"
        />
        <span class="tw-hidden lg:tw-inline">{{ favoriteButton.text }}</span>
      </Button>
    </div>
  </div>
</template>
